import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
    getProjects,
    Project,
    getTasksForProject,
    Task,
    getMasterplanStreams,
    generateScript,
    Script,
} from '../../services/api';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { GlassInput } from '../ui/GlassInput';
import './Scripts.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


interface OutletContext {
    selectedProjectId?: string;
}

const VIDEO_FORMATS = [
    { key: 'short_form', label: 'Short-form', duration: '60-90 sec', description: 'Hook + 3-4 punchy scenes + CTA' },
    { key: 'explainer', label: 'Explainer', duration: '2-3 min', description: 'Intro + scenes with narration & on-screen text + CTA' },
    { key: 'ad_spot', label: 'Ad Spot', duration: '15-30 sec', description: 'Hook + benefit-focused scenes + CTA' },
    { key: 'long_form', label: 'Long-form', duration: '5-10 min', description: 'Intro + scenes with narration & B-roll notes + CTA' },
];

// Normalize stream names for matching
const normalizeStream = (s: string): string =>
    s.replace(/^\d+[\.\)]\s*/, '').replace(/\s*\(.*?\)\s*/g, '').replace(/\s+/g, ' ').trim();

const ScriptCreator: React.FC = () => {
    const navigate = useNavigate();
    const { selectedProjectId } = useOutletContext<OutletContext>();

    const [projects, setProjects] = useState<Project[]>([]);
    const [streams, setStreams] = useState<string[]>([]);
    const [tasks, setTasks] = useState<Task[]>([]);

    const [projectId, setProjectId] = useState(selectedProjectId || '');
    const [selectedStream, setSelectedStream] = useState('');
    const [selectedTaskId, setSelectedTaskId] = useState('');
    const [videoFormat, setVideoFormat] = useState('');
    const [numCharacters, setNumCharacters] = useState(1);
    const [characterNames, setCharacterNames] = useState<string[]>(['']);
    const [additionalDetails, setAdditionalDetails] = useState('');

    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState('');
    const [generatedScript, setGeneratedScript] = useState<Script | null>(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        getProjects().then(setProjects).catch(() => setError('Failed to load projects'));
    }, []);

    useEffect(() => {
        if (selectedProjectId && !projectId) {
            setProjectId(selectedProjectId);
        }
    }, [selectedProjectId]);

    useEffect(() => {
        if (projectId) {
            setSelectedStream('');
            setSelectedTaskId('');
            setStreams([]);
            setTasks([]);
            getMasterplanStreams(projectId)
                .then(res => {
                    const normalized = (res.streams || []).map((s: string) => normalizeStream(s));
                    setStreams(normalized);
                })
                .catch(() => setStreams([]));
        }
    }, [projectId]);

    useEffect(() => {
        if (projectId) {
            setSelectedTaskId('');
            getTasksForProject(projectId)
                .then(allTasks => setTasks(allTasks.filter(t => !!t.synthesized_result)))
                .catch(() => setTasks([]));
        }
    }, [projectId]);

    const handleNumCharactersChange = (n: number) => {
        setNumCharacters(n);
        setCharacterNames(prev => {
            const updated = [...prev];
            while (updated.length < n) updated.push('');
            return updated.slice(0, n);
        });
    };

    // Filter tasks by selected stream if one is chosen
    const filteredTasks = selectedStream
        ? tasks.filter(t => {
            const taskStream = t.input_data?.masterplan_stream;
            if (!taskStream) return false;
            return normalizeStream(taskStream) === selectedStream;
        })
        : tasks;

    const canGenerate = projectId && selectedTaskId && videoFormat && !isGenerating;

    const handleGenerate = async () => {
        if (!canGenerate) return;
        setIsGenerating(true);
        setError('');
        try {
            const script = await generateScript({
                project_id: projectId,
                task_id: selectedTaskId,
                video_format: videoFormat,
                num_characters: numCharacters,
                character_names: characterNames,
                additional_details: additionalDetails,
            });
            setGeneratedScript(script);
        } catch (err: any) {
            setError(getApiError(err, 'Failed to generate script'));
        } finally {
            setIsGenerating(false);
        }
    };

    const handleCopy = () => {
        if (!generatedScript?.script_content) return;
        navigator.clipboard.writeText(generatedScript.script_content).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    if (generatedScript) {
        return (
            <div className="script-creator-container" style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
                <GlassCard variant="default" padding="lg">
                    <div className="script-result">
                        <div className="script-result-header" style={{ marginBottom: '20px' }}>
                            <h2 style={{ margin: '0 0 16px 0', fontSize: '22px', fontWeight: 300 }}>🎬 Script Generated</h2>
                            <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 500 }}>{generatedScript.title}</h3>
                            <div className="result-actions" style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                                <GlassButton
                                    variant="secondary"
                                    onClick={() => generatedScript.script_content && navigator.clipboard.writeText(generatedScript.script_content).then(() => {
                                        setCopied(true);
                                        setTimeout(() => setCopied(false), 2000);
                                    })}
                                >
                                    {copied ? '✓ Copied!' : 'Copy to Clipboard'}
                                </GlassButton>
                                <GlassButton variant="primary" onClick={() => navigate('/scripts')}>
                                    View All Scripts
                                </GlassButton>
                            </div>
                        </div>
                        <pre className="script-content-display" style={{
                            background: 'var(--panel-strong)',
                            padding: '20px',
                            borderRadius: 'var(--radius-md)',
                            fontSize: '14px',
                            lineHeight: 1.6,
                            overflow: 'auto',
                            maxHeight: '60vh',
                            border: '1px solid var(--stroke)'
                        }}>
                            {generatedScript.script_content}
                        </pre>
                    </div>
                </GlassCard>
            </div>
        );
    }

    return (
        <div className="script-creator-container" style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
            <GlassCard variant="default" padding="lg">
                <div className="creator-form">
                    {/* Step 1 — Project */}
                    <div className="form-step" style={{ marginBottom: '24px' }}>
                        <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 500 }}>Step 1 — Project</h3>
                        <div className="form-group">
                            <GlassInput
                                label="Project"
                                value={projectId}
                                onChange={setProjectId}
                                placeholder="Select a project..."
                            />
                        </div>
                    </div>

                    {/* Step 2 — Stream + Article */}
                    <div className={`form-step ${!projectId ? 'disabled' : ''}`} style={{ marginBottom: '24px', opacity: !projectId ? 0.5 : 1 }}>
                        <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 500 }}>Step 2 — Stream &amp; Article</h3>
                        {streams.length > 0 && (
                            <div className="form-group" style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                                    Content Stream (optional)
                                </label>
                                <select
                                    value={selectedStream}
                                    onChange={e => { setSelectedStream(e.target.value); setSelectedTaskId(''); }}
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        fontSize: '14px',
                                        color: 'var(--text)',
                                        background: 'var(--panel)',
                                        border: '1px solid var(--stroke)',
                                        borderRadius: 'var(--radius-md)',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <option value="">All streams</option>
                                    {streams.map(s => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div className="form-group">
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                                Article (with content)
                            </label>
                            <select
                                value={selectedTaskId}
                                onChange={e => setSelectedTaskId(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    fontSize: '14px',
                                    color: 'var(--text)',
                                    background: 'var(--panel)',
                                    border: '1px solid var(--stroke)',
                                    borderRadius: 'var(--radius-md)',
                                    cursor: 'pointer'
                                }}
                            >
                                <option value="">Select an article...</option>
                                {filteredTasks.map(t => (
                                    <option key={t.task_id} value={t.task_id}>{t.name}</option>
                                ))}
                            </select>
                            {projectId && filteredTasks.length === 0 && (
                                <small style={{ color: 'var(--text-muted)', marginTop: '8px', display: 'block' }}>
                                    No articles with content found{selectedStream ? ' in this stream' : ''}.
                                </small>
                            )}
                        </div>
                    </div>

                    {/* Step 3 — Video Format */}
                    <div className={`form-step ${!selectedTaskId ? 'disabled' : ''}`} style={{ opacity: !selectedTaskId ? 0.5 : 1 }}>
                        <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 500 }}>Step 3 — Video Format</h3>
                        <div className="format-options" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                            {VIDEO_FORMATS.map(fmt => (
                                <GlassCard
                                    key={fmt.key}
                                    variant={videoFormat === fmt.key ? 'strong' : 'hover'}
                                    padding="md"
                                    onClick={() => setVideoFormat(fmt.key)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div style={{ fontWeight: 500, marginBottom: '4px' }}>{fmt.label}</div>
                                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>{fmt.duration}</div>
                                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{fmt.description}</div>
                                </GlassCard>
                            ))}
                        </div>

                        <div className="form-group" style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                                Number of Characters
                            </label>
                            <select
                                value={numCharacters}
                                onChange={e => handleNumCharactersChange(Number(e.target.value))}
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    fontSize: '14px',
                                    color: 'var(--text)',
                                    background: 'var(--panel)',
                                    border: '1px solid var(--stroke)',
                                    borderRadius: 'var(--radius-md)',
                                    cursor: 'pointer'
                                }}
                            >
                                {Array.from({ length: 10 }, (_, i) => i + 1).map(n => (
                                    <option key={n} value={n}>{n}</option>
                                ))}
                            </select>
                        </div>

                        {numCharacters > 0 && (
                            <GlassCard variant="strong" padding="md" style={{ marginBottom: '16px' }}>
                                <div className="character-names-group" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    {Array.from({ length: numCharacters }, (_, i) => (
                                        <div key={i}>
                                            <GlassInput
                                                label={`Character ${i + 1} Name`}
                                                placeholder={`e.g. Alex`}
                                                value={characterNames[i] || ''}
                                                onChange={(val) => {
                                                    const updated = [...characterNames];
                                                    updated[i] = val;
                                                    setCharacterNames(updated);
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </GlassCard>
                        )}

                        <div className="form-group" style={{ marginTop: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                                Additional Details <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span>
                            </label>
                            <textarea
                                className="form-textarea"
                                rows={3}
                                placeholder="e.g. Scene directives, character involvement notes, tone guidance…"
                                value={additionalDetails}
                                onChange={e => setAdditionalDetails(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    fontSize: '14px',
                                    fontFamily: 'inherit',
                                    color: 'var(--text)',
                                    background: 'var(--panel)',
                                    border: '1px solid var(--stroke)',
                                    borderRadius: 'var(--radius-md)',
                                    resize: 'vertical',
                                    outline: 'none'
                                }}
                            />
                        </div>
                    </div>

                    {error && (
                        <div style={{
                            padding: '12px 16px',
                            background: 'rgba(248, 113, 113, 0.1)',
                            border: '1px solid var(--danger)',
                            borderRadius: 'var(--radius-md)',
                            color: 'var(--danger)',
                            marginTop: '20px',
                            fontSize: '14px'
                        }}>
                            {error}
                        </div>
                    )}

                    <div className="generate-btn-wrapper" style={{ marginTop: '24px', textAlign: 'center' }}>
                        <GlassButton
                            variant="primary"
                            size="lg"
                            onClick={handleGenerate}
                            disabled={!canGenerate}
                            loading={isGenerating}
                        >
                            {isGenerating ? 'Generating…' : 'Generate Script'}
                        </GlassButton>
                    </div>
                </div>
            </GlassCard>
        </div>
    );
};

export default ScriptCreator;
